import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useSharedLocation } from "@/context/LocationContext";
import { LocationDeniedNote, DistanceBadge } from "@/components/LocationFilterBar";
import { CalendarDays, MapPin, Users, Plus, Trash2, X, CheckCircle2, Phone, Mail, Link as LinkIcon, Globe, Navigation, ChevronLeft, ChevronRight, List, LayoutGrid, Search, Eye, EyeOff } from "lucide-react";
import { AddToCalendarButton } from "@/components/AddToCalendarButton";
import SafetyRulesContent from "@/components/SafetyRulesContent";

const EVENT_CATEGORIES = ["Nature & Outdoors", "Field Trip", "Co-op Class", "Sports", "Arts", "Community", "Other"];
const EMPTY_EVENT = { title: "", description: "", date: "", start_time: "", end_time: "", location: "", category: "Community", capacity: 20, image_url: "", kind: "event", age_range: "" };
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function CalendarGridView({ events, calMonth, setCalMonth, selectedDay, setSelectedDay }) {
  const { year, month } = calMonth;
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = firstDay.getDay();
  const todayStr = new Date().toISOString().slice(0, 10);

  const byDay = {};
  for (const e of events) {
    const d = e.event_date || e.date;
    if (!d) continue;
    (byDay[d] = byDay[d] || []).push(e);
  }

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, dateStr, events: byDay[dateStr] || [] });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => {
    setSelectedDay(null);
    setCalMonth((c) => (c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 }));
  };
  const nextMonth = () => {
    setSelectedDay(null);
    setCalMonth((c) => (c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 }));
  };
  const goToday = () => {
    const n = new Date();
    setCalMonth({ year: n.getFullYear(), month: n.getMonth() });
    setSelectedDay(n.toISOString().slice(0, 10));
  };

  const selectedEvents = selectedDay ? (byDay[selectedDay] || []) : [];

  return (
    <div data-testid="events-calendar-view">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-4 sm:p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <Button size="sm" variant="outline" className="rounded-full border-sage/40" onClick={prevMonth} data-testid="cal-prev-month-btn" aria-label="Previous month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-3">
            <h3 className="text-lg sm:text-xl font-black text-forest font-heading" data-testid="cal-month-label">
              {MONTH_NAMES[month]} {year}
            </h3>
            <Button size="sm" variant="ghost" className="rounded-full text-xs text-sage-dark hover:bg-sage-light" onClick={goToday} data-testid="cal-today-btn">
              Today
            </Button>
          </div>
          <Button size="sm" variant="outline" className="rounded-full border-sage/40" onClick={nextMonth} data-testid="cal-next-month-btn" aria-label="Next month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEKDAYS.map((w) => (
            <div key={w} className="text-center text-[10px] sm:text-xs font-bold uppercase tracking-wide text-forest/50 py-1">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, idx) => {
            if (!cell) return <div key={`e-${idx}`} className="aspect-square" />;
            const isToday = cell.dateStr === todayStr;
            const isSelected = cell.dateStr === selectedDay;
            const hasEvents = cell.events.length > 0;
            return (
              <button
                key={cell.dateStr}
                type="button"
                onClick={() => hasEvents ? setSelectedDay(isSelected ? null : cell.dateStr) : setSelectedDay(cell.dateStr)}
                className={[
                  "aspect-square rounded-xl p-1 sm:p-1.5 flex flex-col items-stretch text-left transition-colors border",
                  isSelected ? "bg-sage text-white border-sage-dark" : isToday ? "bg-terracotta-light border-terracotta/40" : "bg-softstone/40 border-transparent hover:bg-sage-light/60",
                  hasEvents ? "cursor-pointer" : "cursor-default",
                ].join(" ")}
                data-testid={`cal-day-${cell.dateStr}`}
                data-has-events={hasEvents ? "true" : "false"}
              >
                <span className={`text-[11px] sm:text-sm font-bold ${isSelected ? "text-white" : isToday ? "text-terracotta-dark" : "text-forest"}`}>{cell.day}</span>
                {hasEvents && (
                  <div className="flex-1 mt-0.5 space-y-0.5 overflow-hidden">
                    <span className="hidden sm:block truncate text-[10px] leading-tight px-1 py-0.5 rounded bg-terracotta text-white font-semibold" title={cell.events[0].event_name || cell.events[0].title}>
                      {cell.events[0].event_name || cell.events[0].title}
                    </span>
                    {cell.events.length > 1 && (
                      <span className="hidden sm:block text-[9px] text-forest/60 font-semibold px-1">
                        +{cell.events.length - 1} more
                      </span>
                    )}
                    <div className="sm:hidden flex items-center justify-center gap-0.5 mt-1">
                      {cell.events.slice(0, 3).map((_, i) => (
                        <span key={i} className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white" : "bg-terracotta"}`} />
                      ))}
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4 text-[10px] text-forest/50">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-terracotta" /> has events</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-terracotta-light border border-terracotta/40" /> today</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-sage" /> selected</span>
        </div>
      </div>

      {selectedDay && (
        <div className="mt-6" data-testid="cal-selected-day-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-base sm:text-lg font-black text-forest font-heading">
              Events on {new Date(selectedDay + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </h4>
            <button onClick={() => setSelectedDay(null)} className="text-xs font-bold text-terracotta-dark hover:underline" data-testid="cal-clear-day-btn">
              Close
            </button>
          </div>
          {selectedEvents.length === 0 ? (
            <div className="bg-white/60 border border-sage/20 rounded-2xl p-6 text-center text-sm text-forest/60" data-testid="cal-no-events-day">
              No events on this day. Pick another date or switch back to list view.
            </div>
          ) : (
            <div className="space-y-2" data-testid="cal-day-events-list">
              {selectedEvents.map((e) => {
                const title = e.event_name || e.title;
                const time = e.event_time || e.start_time;
                const isQ = e.source === "quicket";
                const Wrap = isQ ? "a" : Link;
                const wrapProps = isQ ? { href: e.external_url, target: "_blank", rel: "noopener noreferrer" } : { to: `/events/${e.event_id}` };
                return (
                  <Wrap
                    key={e.event_id}
                    {...wrapProps}
                    className="block bg-white/80 border border-sage/20 rounded-2xl p-4 hover:shadow-md transition-shadow"
                    data-testid={`cal-day-event-${e.event_id}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-terracotta-light text-terracotta-dark px-2.5 py-1 rounded-full">{e.category}</span>
                      {time && <span className="text-xs font-bold text-forest/70">{time}{e.end_time ? ` – ${e.end_time}` : ""}</span>}
                    </div>
                    <h5 className="font-heading font-bold text-forest mt-1.5">{title}</h5>
                    {e.location && (
                      <p className="text-xs text-forest/60 mt-0.5 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {e.location}
                      </p>
                    )}
                    <div className="mt-2">
                      <AddToCalendarButton event={e} testIdSuffix={`cal-${e.event_id}`} />
                    </div>
                  </Wrap>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [rsvpFor, setRsvpFor] = useState(null);
  const [rsvp, setRsvp] = useState({ children_count: 1, adults_count: 1, children: [], note: "" });
  const [agreeSafety, setAgreeSafety] = useState(false);
  const [showChildDetails, setShowChildDetails] = useState(false);
  const { loc: myLoc, setLoc: setMyLoc, radius, setRadius, locating, denied: locDenied, locateMe, setManual } = useSharedLocation();
  const [locText, setLocText] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  // When a notification routes here with ?event_id=XYZ, show only that card so
  // the user lands directly on the context the notification was about. Any
  // navigation that drops the query param restores the full list.
  const location = useLocation();
  const focusEventId = new URLSearchParams(location.search).get("event_id");
  const [view, setView] = useState("list");
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selectedDay, setSelectedDay] = useState(null);
  const [shownCount, setShownCount] = useState(20);
  useEffect(() => setShownCount(20), [nameSearch, locText, myLoc, radius, focusEventId]);

  const loadReq = useRef(0);
  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (myLoc) {
      params.set("lat", myLoc.lat);
      params.set("lng", myLoc.lng);
      params.set("radius_km", radius);
    }
    const req = ++loadReq.current;
    Promise.all([
      api.get(`/events?${params.toString()}`).then((r) => r.data).catch(() => []),
      api.get(`/quicket-events?${params.toString()}`).then((r) => r.data).catch(() => []),
    ])
      .then(([own, quicket]) => { if (req === loadReq.current) setEvents([...own, ...quicket].sort((a, b) =>
        `${a.date || ""} ${a.start_time || ""}`.localeCompare(`${b.date || ""} ${b.start_time || ""}`))); })
      .finally(() => { if (req === loadReq.current) setLoading(false); });
  }, [myLoc, radius]);
  useEffect(load, [load]);

  const useMyLocation = () => locateMe();
  const setManualLocation = () => setManual(locText);

  const canPost = user && (user.role === "admin" || (user.role === "vendor" && user.vendor_status === "approved"));

  const createEvent = async () => {
    try {
      await api.post("/events", form);
      toast.success("Event posted!");
      setCreateOpen(false);
      setForm(EMPTY_EVENT);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not create event"));
    }
  };

  const submitRsvp = async () => {
    try {
      await api.post(`/events/${rsvpFor.event_id}/rsvp`, rsvp);
      toast.success("RSVP confirmed!");
      setRsvpFor(null);
      setRsvp({ children_count: 1, adults_count: 1, children: [], note: "" });
      setAgreeSafety(false);
      setShowChildDetails(false);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not RSVP"));
    }
  };

  const cancelRsvp = async (eventId) => {
    try {
      await api.delete(`/events/${eventId}/rsvp`);
      toast.success("RSVP cancelled");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not cancel"));
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      await api.delete(`/events/${eventId}`);
      toast.success("Event removed");
      load();
    } catch {
      toast.error("Could not remove event");
    }
  };

  const toggleQuicketHidden = async (e) => {
    try {
      if (e.hidden) await api.delete(`/admin/quicket-events/${e.event_id}/hide`);
      else await api.post(`/admin/quicket-events/${e.event_id}/hide`);
      setEvents((list) => list.map((x) => (x.event_id === e.event_id ? { ...x, hidden: !e.hidden } : x)));
      toast.success(e.hidden ? "Event is visible to families again" : "Event hidden from families");
    } catch (err) {
      toast.error(errMsg(err, "Could not update event"));
    }
  };

  const setChild = (i, key, val) => {
    const arr = [...rsvp.children];
    arr[i] = { ...arr[i], [key]: val };
    setRsvp({ ...rsvp, children: arr });
  };

  const today = new Date().toISOString().slice(0, 10);
  // Dynamic text filter: as the user types in the location input, narrow the
  // visible events to cards whose `location` (city/Google-location string)
  // contains the typed text. Runs on top of the existing radius filter.
  const q = locText.trim().toLowerCase();
  // Name/type/theme search tokens — every token must appear somewhere in the
  // card haystack (title, description, location, date, kind, host, type/theme,
  // and derived human-readable date strings). Partial-word substring matches.
  const nameTokens = nameSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const buildHaystack = (ev) => [
    ev.title, ev.description, ev.location, ev.kind, ev.type, ev.theme,
    ev.host_name, ev.host, ev.category,
    ev.date,
    ev.date ? new Date(ev.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "",
    ev.date ? new Date(ev.date + "T12:00:00").toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "",
  ].filter(Boolean).join(" ").toLowerCase();
  const visible = events
    .filter((e) => (e.kind || "event") !== "meetup" && (e.source === "quicket" ? (e.end_date > e.date ? e.end_date : e.date) : e.date) >= today)
    .filter((e) => !focusEventId || e.event_id === focusEventId)
    .filter((e) => !q || (e.location || "").toLowerCase().includes(q))
    .filter((e) => {
      if (nameTokens.length === 0) return true;
      const hay = buildHaystack(e);
      return nameTokens.every((t) => hay.includes(t));
    });
  const upcoming = visible;

  const EventCard = ({ e }) => {
    const isQuicket = e.source === "quicket";
    const eventPhotoSrc = e.event_photo_url || (e.event_photo ? fileUrl(e.event_photo) : "");
    const organizer = e.organizer_name || e.creator_name;
    const socialLink = (e.social_links || "").trim();
    const socialHref = socialLink && !/^https?:\/\//i.test(socialLink) ? `https://${socialLink}` : socialLink;
    const whenDate = e.event_date || e.date;
    const whenTime = e.event_time || e.start_time;
    const [attendeesOpen, setAttendeesOpen] = useState(false);
    const inlineAttendees = e.attendees || [];
    return (
      <div className="bg-white/80 border border-sage/20 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`event-card-${e.event_id}`}>
        <div className="grid grid-cols-2" data-testid={`event-card-header-${e.event_id}`}>
          <div className="h-48 sm:h-56 bg-sage-light overflow-hidden flex items-center justify-center" data-testid={`event-card-photo-wrap-${e.event_id}`}>
            {eventPhotoSrc ? (
              <img src={eventPhotoSrc} alt={e.event_name || e.title} className="w-full h-full object-contain" data-testid={`event-card-photo-${e.event_id}`} onError={(ev) => { ev.currentTarget.onerror = null; ev.currentTarget.src = "/logo.png"; ev.currentTarget.className = "max-h-28 max-w-[70%] object-contain opacity-80"; }} />
            ) : (
              <img src="/logo.png" alt="Compass Kids logo" className="max-h-28 max-w-[70%] object-contain opacity-80" data-testid={`event-card-photo-empty-${e.event_id}`} />
            )}
          </div>
          <div className="h-48 sm:h-56 bg-cream flex flex-col items-center justify-center gap-1 p-4 text-center border-l border-sage/15" data-testid={`event-card-when-${e.event_id}`}>
            <CalendarDays className="w-6 h-6 text-sage-dark mb-1" />
            <span className="text-2xl sm:text-3xl font-black font-heading text-forest leading-tight" data-testid={`event-date-${e.event_id}`}>{whenDate || "Date TBC"}</span>
            {whenTime && (
              <span className="text-sm font-bold text-forest/70" data-testid={`event-time-${e.event_id}`}>{whenTime}{e.end_time ? ` – ${e.end_time}` : ""}</span>
            )}
          </div>
        </div>
        <div className="p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wide bg-terracotta-light text-terracotta-dark px-2.5 py-1 rounded-full">{e.category}</span>
                {e.kind === "meetup" && (
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-sage text-white px-2.5 py-1 rounded-full" data-testid={`meetup-badge-${e.event_id}`}>Meet-up</span>
                )}
                {e.age_range && (
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full" data-testid={`age-badge-${e.event_id}`}>{e.age_range}</span>
                )}
                <DistanceBadge km={e.distance_km} testid={`event-distance-${e.event_id}`} />
                {e.hidden && (
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-stone-200 text-stone-700 px-2.5 py-1 rounded-full" data-testid={`quicket-hidden-badge-${e.event_id}`}>Hidden from families</span>
                )}
              </div>
              <p className="text-xs font-bold uppercase tracking-wide text-forest/50 mt-3" data-testid={`event-organizer-${e.event_id}`}>{organizer}</p>
              <h3 className="font-heading font-bold text-lg text-forest mt-0.5">
                {isQuicket ? (
                  <a href={e.external_url} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta-dark transition-colors" data-testid={`event-link-${e.event_id}`}>{e.title}</a>
                ) : (
                  <Link to={`/events/${e.event_id}`} className="hover:text-terracotta-dark transition-colors" data-testid={`event-link-${e.event_id}`}>{e.event_name || e.title}</Link>
                )}
              </h3>
              <div className="grid gap-1.5 mt-3 text-xs text-forest/70">
                {e.location && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sage-dark hover:text-terracotta-dark hover:underline transition-colors"
                    data-testid={`event-location-${e.event_id}`}
                    aria-label={`Open ${e.location} in Google Maps`}
                  >
                    <MapPin className="w-3.5 h-3.5 text-sage-dark" />{e.location}
                  </a>
                )}
                {e.contact_number && (
                  <span className="flex items-center gap-2" data-testid={`event-contact-${e.event_id}`}><Phone className="w-3.5 h-3.5 text-sage-dark" /><a href={`tel:${e.contact_number.replace(/\s+/g, "")}`} className="hover:underline">{e.contact_number}</a></span>
                )}
                {(e.email || e.contact_email) && (
                  <span className="flex items-center gap-2" data-testid={`event-email-${e.event_id}`}><Mail className="w-3.5 h-3.5 text-sage-dark" /><a href={`mailto:${e.email || e.contact_email}`} className="hover:underline">{e.email || e.contact_email}</a></span>
                )}
                {socialLink && (
                  <span className="flex items-center gap-2" data-testid={`event-social-${e.event_id}`}><LinkIcon className="w-3.5 h-3.5 text-sage-dark" /><a href={socialHref} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">{socialLink}</a></span>
                )}
                {e.website && (() => {
                  const w = e.website.trim();
                  const href = /^https?:\/\//i.test(w) ? w : `https://${w}`;
                  return (
                    <span className="flex items-center gap-2" data-testid={`event-website-${e.event_id}`}><Globe className="w-3.5 h-3.5 text-sage-dark" /><a href={href} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">{w}</a></span>
                  );
                })()}
              </div>
            </div>
          </div>
          {(e.about_event || e.description) && <p className="text-sm text-forest/70 mt-4 leading-relaxed">{e.about_event || e.description}</p>}
          {isQuicket ? (
          <div className="flex flex-wrap gap-2 mt-4">
            <Button asChild size="sm" variant="outline" className="rounded-full border-sage/40">
              <a href={e.external_url} target="_blank" rel="noopener noreferrer" data-testid={`quicket-tickets-${e.event_id}`}>
                <LinkIcon className="w-4 h-4 mr-1" /> View tickets on Quicket
              </a>
            </Button>
            {user?.role === "admin" && (
              <Button size="sm" variant="ghost" className="rounded-full text-terracotta-dark hover:bg-terracotta-light" onClick={() => toggleQuicketHidden(e)} data-testid={`quicket-hide-toggle-${e.event_id}`}>
                {e.hidden ? <><Eye className="w-4 h-4 mr-1" /> Unhide</> : <><EyeOff className="w-4 h-4 mr-1" /> Hide from families</>}
              </Button>
            )}
          </div>
          ) : (
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              size="sm"
              variant="outline"
              className="rounded-full border-sage/40"
              onClick={() => setAttendeesOpen((o) => !o)}
              aria-expanded={attendeesOpen}
              data-testid={`attendees-toggle-${e.event_id}`}
            >
              <Users className="w-4 h-4 mr-1" />
              {attendeesOpen ? "Hide attendees" : `Show attendees (${e.attendee_count || 0})`}
            </Button>
            {(user?.role === "admin" || user?.user_id === e.created_by) && !(e.event_id || "").startsWith("evt_seed") && (
              <Button size="sm" variant="ghost" className="rounded-full text-terracotta-dark hover:bg-terracotta-light" onClick={() => deleteEvent(e.event_id)} data-testid={`delete-event-${e.event_id}`}>
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            )}
          </div>
          )}
          <div className="mt-4">
            <AddToCalendarButton event={e} testIdSuffix={e.event_id} />
          </div>
          {attendeesOpen && (
            <div className="mt-3 bg-softstone/60 rounded-2xl p-4 space-y-2" data-testid={`attendees-list-${e.event_id}`}>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-sage-dark" />
                <h4 className="text-sm font-bold text-forest" data-testid={`attendees-heading-${e.event_id}`}>
                  Attendees ({e.attendee_count || 0})
                </h4>
              </div>
              {inlineAttendees.length === 0 && (
                <p className="text-xs text-forest/50" data-testid={`no-attendees-${e.event_id}`}>No one has RSVPed yet.</p>
              )}
              {inlineAttendees.map((a) => {
                const parts = (a.parent_name || "").trim().split(/\s+/);
                const surname = parts.length > 1 ? parts[parts.length - 1] : (parts[0] || "Family");
                const familyLabel = `The ${surname}${surname.endsWith("s") ? "" : "s"}`;
                const kids = a.family_children || [];
                const adultsCount = a.adults_count || 0;
                return (
                  <div key={a.rsvp_id} className="text-sm text-forest/80 flex flex-wrap gap-x-3" data-testid={`attendee-row-${a.rsvp_id}`}>
                    <span className="font-bold" data-testid={`attendee-family-${a.rsvp_id}`}>{familyLabel}</span>
                    <span data-testid={`attendee-adults-${a.rsvp_id}`}>
                      {adultsCount} adult{adultsCount === 1 ? "" : "s"}
                    </span>
                    {kids.map((k, i) => {
                      const bits = [];
                      if (k.age !== "" && k.age !== undefined && k.age !== null) bits.push(`age ${k.age}`);
                      if (k.gender) bits.push(k.gender);
                      return (
                        <span key={i} className="text-forest/60" data-testid={`attendee-child-${a.rsvp_id}-${i}`}>
                          {bits.join(" · ") || `child ${i + 1}`}
                        </span>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="events-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">Community calendar</p>
          <h1 className="text-3xl sm:text-4xl font-black text-forest mt-1">Events & activities</h1>
          <p className="text-forest/60 mt-1">
            Hikes, field trips, co-op classes — sign your kids up below. Looking for casual parent-hosted hangouts?{" "}
            <Link to="/meetups" className="font-bold text-terracotta-dark hover:underline" data-testid="goto-meetups-link">Browse meet-ups</Link>
          </p>
        </div>
        {canPost && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-sage hover:bg-sage-dark text-white shadow-md" data-testid="create-event-btn">
                <Plus className="w-4 h-4 mr-2" /> Post an event
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl bg-cream max-h-[90vh] overflow-y-auto" data-testid="create-event-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-forest">Post a new event</DialogTitle>
                <DialogDescription className="sr-only">Fill in the details to publish a community event.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-forest/80">Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30" data-testid="event-title-input" />
                </div>
                <div>
                  <Label className="text-forest/80">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1 rounded-xl bg-white border-sage/30" data-testid="event-desc-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-forest/80">Date *</Label>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30" data-testid="event-date-input" />
                  </div>
                  <div>
                    <Label className="text-forest/80">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="mt-1 rounded-xl bg-white border-sage/30" data-testid="event-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {EVENT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-forest/80">Start time</Label>
                    <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30" data-testid="event-start-input" />
                  </div>
                  <div>
                    <Label className="text-forest/80">End time</Label>
                    <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30" data-testid="event-end-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-forest/80">Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30" data-testid="event-location-input" />
                </div>
                <div>
                  <Label className="text-forest/80">Attendee limit (children spots)</Label>
                  <Input type="number" min={1} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 1 })} className="mt-1 rounded-xl bg-white border-sage/30" data-testid="event-capacity-input" />
                </div>
                <Button onClick={createEvent} disabled={!form.title || !form.date} className="w-full rounded-full bg-sage hover:bg-sage-dark text-white" data-testid="event-submit-btn">
                  Publish event
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-4 sm:p-5 mb-4 shadow-sm" data-testid="events-name-filter">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
          <Input
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            placeholder="Search by name, type, theme, date..."
            className="pl-9 rounded-full bg-softstone/60 border-sage/30"
            data-testid="events-name-search-input"
          />
        </div>
      </div>

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-5 sm:p-6 mb-8 shadow-sm" data-testid="events-location-filter">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
              <Input
                value={locText}
                onChange={(e) => setLocText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setManualLocation()}
                placeholder="Your suburb or area, e.g. Roodepoort"
                className="pl-9 rounded-full bg-softstone/60 border-sage/30"
                data-testid="events-location-input"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full shrink-0 flex-1 sm:flex-none" onClick={setManualLocation} disabled={locating || locText.trim().length < 3} data-testid="events-set-location-btn">
                Set
              </Button>
              <Button variant="outline" className="rounded-full shrink-0 flex-1 sm:flex-none border-sage/40" onClick={useMyLocation} disabled={locating} data-testid="events-use-location-btn">
                <Navigation className="w-4 h-4 mr-1" /> {locating ? "Locating..." : "Use my location"}
              </Button>
            </div>
          </div>
        </div>
        {locDenied && <LocationDeniedNote testid="events-location-denied" />}
        {myLoc && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs font-semibold text-forest/60 mb-2">
              <span data-testid="events-current-location-label">Near: {myLoc.label}</span>
              <span className="text-forest" data-testid="events-radius-value">Within {radius} km</span>
            </div>
            <Slider value={[radius]} onValueChange={(v) => setRadius(v[0])} min={5} max={100} step={5} className="w-full" data-testid="events-radius-slider" />
            <div className="flex justify-between text-[10px] text-forest/40 mt-1">
              <span>5 km</span><span>100 km</span>
            </div>
            <button onClick={() => setMyLoc(null)} className="text-xs font-bold text-terracotta-dark hover:underline mt-2" data-testid="events-clear-location-btn">
              Clear location filter
            </button>
          </div>
        )}
        {!myLoc && !locDenied && (
          <p className="text-xs text-forest/50 mt-3">Set your location to see events near you — the distance slider defaults to 20 km and goes up to 100 km.</p>
        )}
      </div>

      <div className="flex items-center gap-2 mb-6" data-testid="events-view-toggle">
        <Button
          size="sm"
          variant={view === "list" ? "default" : "outline"}
          onClick={() => setView("list")}
          className={`rounded-full ${view === "list" ? "bg-sage hover:bg-sage-dark text-white" : "border-sage/40"}`}
          data-testid="events-view-list-btn"
        >
          <List className="w-4 h-4 mr-1.5" /> List view
        </Button>
        <Button
          size="sm"
          variant={view === "calendar" ? "default" : "outline"}
          onClick={() => setView("calendar")}
          className={`rounded-full ${view === "calendar" ? "bg-sage hover:bg-sage-dark text-white" : "border-sage/40"}`}
          data-testid="events-view-calendar-btn"
        >
          <LayoutGrid className="w-4 h-4 mr-1.5" /> Calendar grid
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-forest/50">Loading events...</div>
      ) : view === "calendar" ? (
        <CalendarGridView
          events={visible}
          calMonth={calMonth}
          setCalMonth={setCalMonth}
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
        />
      ) : (
        <>
          <div className="space-y-4">
            {upcoming.length === 0 && (
              <div className="bg-white/60 border border-sage/20 rounded-3xl p-12 text-center text-forest/60" data-testid="no-upcoming-events">
                <CalendarDays className="w-10 h-10 text-sage mx-auto mb-3" />
                No upcoming events. Check back soon!
              </div>
            )}
            {upcoming.slice(0, shownCount).map((e) => <EventCard key={e.event_id} e={e} />)}
          </div>
          {upcoming.length > shownCount && (
            <div className="flex flex-col items-center gap-1 mt-6">
              <Button variant="outline" className="rounded-full border-sage/40" onClick={() => setShownCount((n) => n + 20)} data-testid="events-load-more-btn">
                Load more events
              </Button>
              <span className="text-xs text-forest/50" data-testid="events-shown-count">Showing {shownCount} of {upcoming.length}</span>
            </div>
          )}
        </>
      )}

      <Dialog open={!!rsvpFor} onOpenChange={(o) => { if (!o) { setRsvpFor(null); setAgreeSafety(false); } }}>
        <DialogContent className="rounded-3xl bg-cream" data-testid="rsvp-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">RSVP: {rsvpFor?.title}</DialogTitle>
            <DialogDescription className="sr-only">Register your children for this event.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-forest/60">
              Registering as <strong>{user?.name}</strong> · {rsvpFor ? rsvpFor.capacity - rsvpFor.spots_filled : 0} spots left
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-forest/80">Number of children *</Label>
                <Input type="number" min={1} max={10} value={rsvp.children_count}
                  onChange={(e) => setRsvp({ ...rsvp, children_count: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="mt-1 rounded-xl bg-white border-sage/30" data-testid="rsvp-children-count" />
              </div>
              <div>
                <Label className="text-forest/80">Number of adults *</Label>
                <Input type="number" min={0} max={10} value={rsvp.adults_count}
                  onChange={(e) => setRsvp({ ...rsvp, adults_count: Math.max(0, parseInt(e.target.value) || 0) })}
                  className="mt-1 rounded-xl bg-white border-sage/30" data-testid="rsvp-adults-count" />
              </div>
            </div>
            {!showChildDetails ? (
              <button type="button" onClick={() => { setShowChildDetails(true); setRsvp((r) => ({ ...r, children: Array.from({ length: r.children_count }, (_, i) => r.children[i] || { name: "", age: "" }) })); }}
                className="text-sm font-bold text-sage-dark hover:underline" data-testid="add-child-details-btn">
                + Add child details (optional)
              </button>
            ) : (
              <div className="space-y-2" data-testid="child-details">
                {rsvp.children.map((c, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={c.name} onChange={(e) => setChild(i, "name", e.target.value)} placeholder={`Child ${i + 1} name`} className="rounded-xl bg-white border-sage/30" data-testid={`child-name-${i}`} />
                    <Input value={c.age} onChange={(e) => setChild(i, "age", e.target.value)} placeholder="Age" className="w-20 rounded-xl bg-white border-sage/30" data-testid={`child-age-${i}`} />
                    <button type="button" onClick={() => setRsvp({ ...rsvp, children: rsvp.children.filter((_, j) => j !== i) })} className="p-2 text-terracotta-dark" aria-label="Remove child">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <Label className="text-forest/80">Note for the organizer</Label>
              <Textarea value={rsvp.note} onChange={(e) => setRsvp({ ...rsvp, note: e.target.value })} rows={2}
                placeholder="Allergies, questions, anything to know..." className="mt-1 rounded-xl bg-white border-sage/30" data-testid="rsvp-note-input" />
            </div>
            <div className="rounded-xl bg-softstone/50 border border-sage/20 p-3 space-y-2" data-testid="rsvp-safety-block">
              <details className="group" data-testid="rsvp-safety-details">
                <summary className="cursor-pointer text-xs font-bold text-terracotta-dark select-none">
                  Read the Compass Kids safety rules
                </summary>
                <div className="mt-3 max-h-64 overflow-y-auto pr-2 border-t border-sage/20 pt-3">
                  <SafetyRulesContent variant="compact" testIdPrefix="rsvp-safety-rules" />
                </div>
              </details>
              <label className="flex items-start gap-2 text-xs text-forest/80 cursor-pointer" data-testid="rsvp-agree-safety-label">
                <input
                  type="checkbox"
                  checked={agreeSafety}
                  onChange={(e) => setAgreeSafety(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-sage-dark cursor-pointer shrink-0"
                  data-testid="rsvp-agree-safety-checkbox"
                />
                <span>
                  I have read and agree to the{" "}
                  <Link to="/safety-rules" target="_blank" rel="noopener noreferrer" className="font-bold text-terracotta-dark hover:underline" data-testid="rsvp-safety-rules-link">
                    Compass Kids safety rules
                  </Link>
                  , including meeting in a public venue for first-time meet-ups and telling a trusted
                  person where I am going. I understand I'm responsible for supervising my own children.
                </span>
              </label>
            </div>
            <Button onClick={submitRsvp} disabled={!agreeSafety} className="w-full rounded-full bg-terracotta hover:bg-terracotta-dark text-white" data-testid="rsvp-submit-btn">
              Confirm RSVP
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
